import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../api';

type LoginUsernameParams = {
  username: string;
  password: string;
};

type LoginParams = {
  username?: string;
  phone?: string;
  email?: string;
  password?: string;
};

type LoginEmailParams = {
  email: string;
  password: string;
};

const loginEmail = async ({ email, password }: LoginEmailParams) => {
  const profileData = await supabase
    .from('user_profiles')
    .select()
    .is('is_deleted', null)
    .eq('email', email)
    .single();

  if (profileData.error) {
    throw new Error('Check the correctness of your email');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw new Error('Incorrect password');
  }
  // @ts-ignore
  if (window.analytics) {
    // @ts-ignore
    window.analytics.identify(data.user?.id, {
      name: data.user?.user_metadata.full_name,
      email: data.user?.email,
    });
  }

  return data;
};

const loginUsername = async ({ username, password }: LoginUsernameParams) => {
  const profileData = await supabase
    .from('user_profiles')
    .select('email')
    .is('is_deleted', null)
    .ilike('username', username)
    .single();

  if (profileData.error) {
    throw new Error('Check the correctness of your email');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: profileData.data.email,
    password,
  });

  if (error) {
    throw new Error('Incorrect password');
  }

  // @ts-ignore
  if (window.analytics) {
    // @ts-ignore
    window.analytics.identify(data.user?.id, {
      name: data.user?.user_metadata.full_name,
      email: data.user?.email,
    });
  }

  return data;
};

class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhoneDoesNotExist';
  }
}

const loginPhone = async (phone: string) => {
  const { data: existData, error: existError } = await supabase
    .from('user_profiles')
    .select('id')
    .is('is_deleted', null)
    .eq('phone', (phone?.replace('+', '') || '').replace(/\D/g, ''));

  if (!existData?.length) {
    throw new LoginError(existError?.message || '');
  }

  const { data, error } = await supabase.auth.signInWithOtp({
    phone: phone?.replace('+', ''),
  });

  if (error) {
    throw new Error(error.message);
  }

  // @ts-ignore
  if (window.analytics) {
    // @ts-ignore
    const user = data.user as any;
    window.analytics.identify(user?.id, {
      name: user?.user_metadata?.full_name,
      email: user?.email,
    });
  }

  return data;
};

export default function useLogin({
  username = '',
  email = '',
  password = '',
  phone = '',
}: LoginParams) {
  const queryClient = useQueryClient();

  return useMutation(
    async () => {
      if (phone) return await loginPhone(phone);
      if (username) return await loginUsername({ username, password });
      return await loginEmail({ email, password });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['isAuth']);
        queryClient.removeQueries({ queryKey: ['user'] });
        queryClient.removeQueries({ queryKey: ['profile'] });
        queryClient.removeQueries({ queryKey: ['user-categories'] });
        queryClient.invalidateQueries({
          queryKey: ['user', 'profile', 'isAuth', 'user-categories'],
        });
      },
    },
  );
}
